import { useState, useRef, useCallback, useEffect } from 'react';
import { OdbcJobStatus } from '../widget/types';

interface UseJobPollerOptions {
  pollInterval?: number;
  onComplete?: (job: OdbcJobStatus) => void;
  onFailed?: (job: OdbcJobStatus) => void;
}

export function useJobPoller(
  pollFn: (jobId: number) => Promise<OdbcJobStatus>,
  options: UseJobPollerOptions = {}
) {
  const { pollInterval = 1500, onComplete, onFailed } = options;
  const [activeJob, setActiveJob] = useState<OdbcJobStatus | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollFnRef = useRef(pollFn);
  pollFnRef.current = pollFn;

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const startPolling = useCallback((jobId: number) => {
    stopPolling();
    setActiveJob({ id: jobId, action: '', status: 'pending', result_message: null, error_message: null, created_at: '', completed_at: null });

    const poll = async () => {
      try {
        const job = await pollFnRef.current(jobId);
        setActiveJob(job);
        if (job.status === 'completed') {
          stopPolling();
          onComplete?.(job);
        } else if (job.status === 'failed') {
          stopPolling();
          onFailed?.(job);
        }
      } catch (err) {
        stopPolling();
      }
    };

    poll();
    intervalRef.current = setInterval(poll, pollInterval);
  }, [pollInterval, stopPolling, onComplete, onFailed]);

  const clearJob = useCallback(() => {
    stopPolling();
    setActiveJob(null);
  }, [stopPolling]);

  useEffect(() => {
    return () => stopPolling();
  }, [stopPolling]);

  return {
    startPolling,
    stopPolling,
    clearJob,
    activeJob,
    isPolling: intervalRef.current !== null,
    isComplete: activeJob?.status === 'completed',
    isFailed: activeJob?.status === 'failed',
    isPending: activeJob?.status === 'pending' || activeJob?.status === 'processing',
  };
}
